/**
 * shapeAdapters — the Festipod DOMAIN mapping from the SDK's generic per-subject
 * property bags (`UnionSubject`, the shape of what `watchShape` yields) to the
 * app's `Fp*` entity types. This is the READ-side domain glue: `watchShape` is
 * non-domain (it filters a scope's docs by a SHEX `@type` and returns the raw
 * property bags); the app owns the interpretation of the `fp:` predicates.
 *
 * The mapping logic lives HERE (in the app), not in the SDK — the SDK stays a
 * finished NextGraph surface that knows nothing of Festipod's fields. This file
 * previously lived in `readEntities.ts` alongside a bespoke `readModel.readUnion`
 * call; the read machinery is now the SDK's `watchShape`, so only the domain
 * mapping remains, extracted here.
 */

import type { UnionSubject } from '@ng-eventually/polyfill';
import type { FpEventData, FpUserData, FpParticipationData } from './types';

const FP = 'http://festipod.org/';

/** First object value of a `fp:` predicate on a subject (or `fallback`). */
function one(s: UnionSubject, field: string, fallback = ''): string {
  return s.props[`${FP}${field}`]?.[0] ?? fallback;
}
function num(s: UnionSubject, field: string, fallback = 0): number {
  const v = s.props[`${FP}${field}`]?.[0];
  const n = v === undefined ? NaN : Number(v);
  return Number.isFinite(n) ? n : fallback;
}
function boolOf(s: UnionSubject, field: string): boolean {
  return (s.props[`${FP}${field}`]?.[0] ?? 'false') === 'true';
}

/** Map a generic subject (Event shape) to `FpEventData`. */
export function adaptEvent(s: UnionSubject): FpEventData {
  return {
    id: s.subject,
    title: one(s, 'title'),
    description: one(s, 'description'),
    date: one(s, 'date'),
    location: one(s, 'location'),
    distance: s.props[`${FP}distance`] ? num(s, 'distance') : undefined,
    participantCount: num(s, 'participantCount'),
    coverImage: one(s, 'coverImage') || undefined,
  };
}

/** Map a generic subject (UserProfile shape) to `FpUserData`. */
export function adaptUser(s: UnionSubject): FpUserData {
  return {
    id: s.subject,
    name: one(s, 'name'),
    initials: one(s, 'initials'),
    username: one(s, 'username'),
    role: one(s, 'role') || undefined,
    isPublic: s.props[`${FP}isPublic`] ? boolOf(s, 'isPublic') : undefined,
  };
}

/**
 * Map every Event-shape subject to `FpEventData`.
 */
export function adaptEvents(subjects: UnionSubject[]): FpEventData[] {
  return subjects.map(adaptEvent);
}

/**
 * Map every UserProfile-shape subject to `FpUserData`.
 */
export function adaptUsers(subjects: UnionSubject[]): FpUserData[] {
  return subjects.map(adaptUser);
}

/**
 * Map every Participation-shape subject to `FpParticipationData`, DROPPING any
 * participation that carries no `fp:user` (the SHEX `fp:user` is mandatory — a
 * participation without a user principal is malformed and must never round-trip;
 * this matches the historical ORM/read behavior).
 */
export function adaptParticipations(subjects: UnionSubject[]): FpParticipationData[] {
  const out: FpParticipationData[] = [];
  for (const s of subjects) {
    const userId = one(s, 'user');
    if (!userId) continue; // fp:user mandatory — drop malformed participations
    out.push({
      id: s.subject,
      eventId: one(s, 'event'),
      userId,
      isConfirmed: boolOf(s, 'isConfirmed'),
    });
  }
  return out;
}
